/**
 * @fileoverview Reading a collection: one address in, one site out, and the two
 * views of it that must never be combined.
 *
 * Follows the page loader's discipline deliberately rather than inventing its
 * own, because the properties that matter are the same ones. Every failure
 * collapses to a single unavailable answer held to a common timing floor, so
 * neither the reason nor the elapsed time tells a stranger anything; and the
 * address that was ASKED FOR is authoritative over whatever the relay chose to
 * reply with.
 *
 * ---
 *
 * THE ONE RULE THAT IS SPECIFIC TO COLLECTIONS: NEVER COMBINE THE TWO LISTS.
 *
 * A collection with private members holds its order twice. The plaintext tags
 * carry the public members; the sealed half carries the COMPLETE list, public
 * members included. That is not redundancy to be optimised away, it is what
 * removes the merge:
 *
 *   no key   -> the tags are the whole truth
 *   key      -> the sealed list is the whole truth
 *
 * A reader that tried to be clever, taking the tags and splicing the private
 * entries in, would be re-implementing the anchoring rule the format was
 * reshaped to delete, and would reintroduce its failure: an anchor that no
 * longer exists, or two lists that disagree, producing an order the author never
 * chose. The publisher is held to the same rule from the other side, by a check
 * that refuses to write an event whose two orders differ.
 *
 * ---
 *
 * WHAT A READER WITHOUT THE KEY GETS IS A REAL SITE, NOT A DEGRADED ONE. The
 * public members, in the author's order, with nothing saying how much is not
 * being shown. That is the same promise the event format makes and this is where
 * it is kept: no count, no gap, no placeholder.
 *
 * The one thing the reader is told is whether an unopened sealed half was
 * present, and that is for the OWNER's benefit rather than a stranger's: "there
 * is nothing private in this collection" and "I could not open the private part"
 * are very different things to show the person who wrote it, and identical to
 * everybody else.
 *
 * ---
 *
 * HOW THE KEY ARRIVES, AND IN WHICH ORDER IT IS LOOKED FOR.
 *
 *   1. The OWNER's copy rides on the collection event itself, so an author opens
 *      their own site with no further lookup at all.
 *   2. A RECIPIENT's copy travels on a share event addressed to them, which
 *      costs a second relay query, so it is only attempted when the first fails.
 *
 * THE ORDER IS THE WHOLE OPTIMISATION and it is worth stating: the common case
 * is an author reading their own collection, and that case must not pay for a
 * share lookup. The uncommon case is a recipient, and a second query is a fair
 * price for the thing that makes sharing work at all.
 *
 * A READER WITH NO SIGNER SKIPS BOTH, which is the flagship case: a stranger
 * with a link and no account gets the public half and never touches a key.
 */

import type { ContentKey, SignerInterface } from '@cloistr/auth/core';


import { type ParsedCollection, parseCollectionEvent } from './collectionEvent';
import { readOwnerKey } from './collectionKeys';
import { PAGE_COLLECTION_KIND } from './kinds';
import { sharedKeyFor } from './shareCollection';

import { decodeIdentifier } from '@/services/nostr/identifiers';
import { fetchNewest, relaysFor } from '@/services/nostr/relays';

export type CollectionLoad =
  | {
      status: 'ready';
      collection: ParsedCollection;
      /** The collection's own address, rebuilt from what was asked for. */
      address: { kind: number; pubkey: string; identifier: string };
      /**
       * The relays this collection was found on, carried out for the same
       * reason the page loader carries them: a follow-up fetch, for the theme
       * or for a member, should ask the relays that already proved they know
       * about this author rather than defaulting to ours.
       */
      relays: string[];
    }
  | { status: 'unavailable' };

/**
 * The floor every unavailable answer is held to.
 *
 * Copied from the page loader ON PURPOSE and not shared with it: if the two
 * floors were one constant, a future change tuning one would silently move the
 * other, and the thing being protected is that a collection failure and a page
 * failure cannot be told apart by how long they took.
 */
const UNAVAILABLE_FLOOR_MS = 900;

const UNAVAILABLE: CollectionLoad = { status: 'unavailable' };

export async function loadCollection(
  naddr: string,
  options: { signer?: SignerInterface; now?: () => number } = {}
): Promise<CollectionLoad> {
  const now = options.now ?? (() => Date.now());
  const startedAt = now();
  const result = await attempt(naddr, options.signer);
  if (result.status === 'ready') return result;

  const elapsed = now() - startedAt;
  if (elapsed < UNAVAILABLE_FLOOR_MS) {
    await new Promise((resolve) => setTimeout(resolve, UNAVAILABLE_FLOOR_MS - elapsed));
  }
  return UNAVAILABLE;
}

async function attempt(
  naddr: string,
  signer: SignerInterface | undefined
): Promise<CollectionLoad> {
  let decoded;
  try {
    decoded = decodeIdentifier(naddr);
  } catch {
    // Someone has put a private key in a URL. Nothing renders, nothing is echoed.
    return UNAVAILABLE;
  }

  // An address for any other kind is a real reference to something that is not
  // a collection. Not routed here, and not distinguished from a miss.
  if (!decoded || decoded.type !== 'address' || decoded.kind !== PAGE_COLLECTION_KIND) {
    return UNAVAILABLE;
  }

  const relays = relaysFor(decoded.relays);
  const event = await fetchNewest(relays, {
    kinds: [PAGE_COLLECTION_KIND],
    authors: [decoded.pubkey],
    '#d': [decoded.identifier],
  });
  if (!event) return UNAVAILABLE;

  // THE ADDRESS ASKED FOR IS AUTHORITATIVE, NOT THE RELAY'S ANSWER, and it
  // matters more here than for a single page. An address carries its own relay
  // hints, chosen by whoever made the link, so a shared link can name a relay
  // the sharer runs. Unchecked, that relay could answer with its own collection
  // and have it render under this author's address. For a page that
  // misrepresents one document; for a collection it misrepresents an entire
  // SITE, including which pages the author is presented as endorsing.
  if (event.pubkey !== decoded.pubkey) return UNAVAILABLE;
  const identifier = event.tags.find((t) => t[0] === 'd')?.[1];
  if (identifier !== decoded.identifier) return UNAVAILABLE;

  const address = {
    kind: PAGE_COLLECTION_KIND,
    pubkey: decoded.pubkey,
    identifier: decoded.identifier,
  };

  // The key: the owner's copy first, then a share addressed to this reader.
  let key: ContentKey | undefined;
  if (signer && event.content) {
    const coordinate = `${PAGE_COLLECTION_KIND}:${decoded.pubkey}:${decoded.identifier}`;
    try {
      key = (await readOwnerKey(event.tags, coordinate, signer)) ?? undefined;
    } catch {
      // A key tag that is present and does not open means this reader is not
      // the owner, which is the ordinary case for a recipient. NOT a reason to
      // fail: fall through to the share lookup below.
      key = undefined;
    }

    if (!key) {
      try {
        key = (await sharedKeyFor(coordinate, signer, relays)) ?? undefined;
      } catch {
        // A share lookup that fails leaves the reader exactly where a stranger
        // is, which is a real answer rather than an error.
        key = undefined;
      }
    }
  }

  const collection = parseCollectionEvent(event, key);
  // A collection event that is missing its name or its title is not a
  // collection, and lands where every other failure lands.
  if (!collection) return UNAVAILABLE;

  return { status: 'ready', collection, address, relays };
}
